import { useCallback, useEffect, useRef, useState } from "react";
import {
	type AssistantStyleDto,
	DEFAULT_ASSISTANT_STYLE,
} from "@/types/ai-assistant-style";
import { AdminEndpointUnavailableError, fetchStyle } from "./ai-assistant.api";

const defaults = (): AssistantStyleDto => ({
	...DEFAULT_ASSISTANT_STYLE,
	expressiveWords: [...DEFAULT_ASSISTANT_STYLE.expressiveWords],
	voiceWaitPhrases: [...DEFAULT_ASSISTANT_STYLE.voiceWaitPhrases],
	extraRules: [],
});

/** Muat gaya & larangan tambahan; endpoint belum ada → nilai default, form terkunci. */
export function useAssistantStyle() {
	const [style, setStyle] = useState<AssistantStyleDto>(defaults);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [unavailable, setUnavailable] = useState(false);
	const abortRef = useRef<AbortController | null>(null);

	const refresh = useCallback(async () => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setLoading(true);
		setError(null);
		try {
			setStyle(await fetchStyle(controller.signal));
			setUnavailable(false);
		} catch (err) {
			if (err instanceof Error && err.name === "AbortError") return;
			if (err instanceof AdminEndpointUnavailableError) {
				setStyle(defaults());
				setUnavailable(true);
			} else {
				setError(err instanceof Error ? err.message : "Terjadi kesalahan");
			}
		} finally {
			if (!controller.signal.aborted) setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
		return () => abortRef.current?.abort();
	}, [refresh]);

	return { style, loading, error, unavailable, refresh, apply: setStyle };
}
